import http from "axiosSetup";
import { Circuit, CircuitAdd, CircuitEdit } from "types/circuit";
import logEvent from "utils/logEvent";

/**
 * CRUD for circuits, served entirely by the measurement service on v1
 * (`/api/v1/circuits`) — circuits were never upgraded to v2, so unlike
 * {@link CustomExerciseService} every call goes through `http`, not `apiV2`.
 *
 * `create`/`update` send only the high-level inputs; the server generates the
 * circuit's `metrics[]`/`roundMetrics[]` and returns the new id, so both re-fetch
 * via `getById` to resolve the generated circuit.
 */
class CircuitService {
  static async getAll() {
    return http.get<Circuit[]>("/circuits").then((response) => response.data);
  }

  static async getById(id: string) {
    return http.get<Circuit>(`/circuits/${id}`).then((response) => response.data);
  }

  static async create(circuit: CircuitAdd) {
    const result = await http.post<{ id: string }>("/circuits", circuit).then((response) => response.data);
    logEvent("CIRCUIT_CREATE");

    return CircuitService.getById(result.id);
  }

  static async update(id: string, circuit: CircuitEdit) {
    const result = await http.put<{ id: string }>(`/circuits/${id}`, circuit).then((response) => response.data);
    logEvent("CIRCUIT_UPDATE");

    return CircuitService.getById(result.id);
  }

  static async delete(id: string) {
    await http.delete(`/circuits/${id}`);
    logEvent("CIRCUIT_DELETE");

    return { id };
  }
}

export default CircuitService;
